import React, { useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { withAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlassian/jira-feature-gating';
import fetchJson from '@atlassian/jira-issue-reliability-fetch/src/issue-view-as-json.tsx';
import type { Transition } from '@atlassian/jira-issue-fetch-services/src/types.tsx';
import { useFieldsValues } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { trackOrLogClientError } from '@atlassian/jira-issue-view-errors/src/common/utils/track-error.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import { WorkflowTransitionsStoreContainer } from '@atlassian/jira-issue-workflow-transitions-services/src/context.tsx';
import { useWorkflowTransitionsValue } from '@atlassian/jira-issue-workflow-transitions-services/src/main.tsx';
import { fireTrackAnalyticsDeferred } from '@atlassian/jira-product-analytics-bridge';
import { useAnalyticsSource } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Action } from '@atlassian/jira-experience-tracker/src/common/constants.tsx';
import {
	useIsBusinessIssue,
	useIsSoftwareIssue,
} from '@atlassian/jira-issue-util-hooks/src/index.tsx';
import { STATUS_FIELD_FETCH_TRANSITIONS_KEY } from '../../common/constants.tsx';
import { transitionIssueExperienceDescription } from '../experience-descriptions/index.tsx';
import { useFetchTransitionsForStatusField } from '../transitions-relay-query/index.tsx';
import type { Props } from './types.tsx';
import {
	filterTransitionsWithDestinationAndCategory,
	transformTransitionToStore,
	transformTransitionFromStore,
} from './utils.tsx';

type IssueKey = string;

export const getStatusTransitionsUrl = (issueKey: IssueKey) =>
	`/rest/api/2/issue/${issueKey}/transitions?sortByOpsBarAndStatus=true`;

const enhancedSendExperienceAnalytics = (
	successful: boolean,
	projectType?: ProjectType | null,
	errorMessage?: undefined | string,
	traceId?: string,
	statusCode?: number,
	source?: string,
	action?: Action,
) => {
	const description = transitionIssueExperienceDescription(
		successful,
		'GET',
		source ?? '',
		projectType || null,
		errorMessage,
		traceId,
		statusCode,
		action,
	);
	sendExperienceAnalytics(description);
};

const BaseTransitionsQuery = (props: Props) => {
	const { status, issueKey, projectType, preFetch, createAnalyticsEvent, isSaving, source } = props;
	const [transitionsState, setTransitionsState] = useWorkflowTransitionsValue();
	const isSingletonRequestRef = useRef(false);

	// This is a workaround to ensure that the loading state is up to date in useEffect
	// without it leading to an infinite loop.
	const isTransitionStateLoading = useRef(transitionsState.loading);
	isTransitionStateLoading.current = transitionsState.loading;

	const isBusinessIssue = useIsBusinessIssue();
	const isSoftwareIssue = useIsSoftwareIssue();

	const isStatusMoveExperimentEnabled = useMemo(() => {
		return (
			(isBusinessIssue || isSoftwareIssue) &&
			expVal('move_status_to_summary_ga', 'isEnabled', false)
		);
	}, [isBusinessIssue, isSoftwareIssue]);

	const [fieldValues] = useFieldsValues(issueKey);
	const { fetchTransitions } = fg('relay-migration-issue-fields-status')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useFetchTransitionsForStatusField({ issueKey })
		: { fetchTransitions: () => [] };

	const fetch = useCallback(
		async (event: UIAnalyticsEvent) => {
			if (!isStatusMoveExperimentEnabled && transitionsState.loading) {
				return;
			}

			if (isStatusMoveExperimentEnabled && isTransitionStateLoading?.current) {
				return;
			}
			if (isSingletonRequestRef.current) {
				return;
			}
			isSingletonRequestRef.current = true;

			setTransitionsState({
				loading: true,
				error: null,
			});

			try {
				let transitions: Transition[];

				if (fg('relay-migration-issue-fields-status')) {
					transitions = await fetchTransitions();
				} else {
					const result = await fetchJson(
						getStatusTransitionsUrl(issueKey),
						{},
						'fields/status/src/services/transitions-query/main',
					);
					transitions = filterTransitionsWithDestinationAndCategory(result.transitions).map(
						transformTransitionToStore,
					);
				}

				fireTrackAnalyticsDeferred(event, 'statusField transitionsFetched', 'statusField', {
					count: transitions.length,
				});

				// Issue Transition SLA
				enhancedSendExperienceAnalytics(true, projectType, undefined, undefined, undefined, source);

				setTransitionsState({
					data: transitions,
					loading: false,
					error: null,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				trackOrLogClientError(STATUS_FIELD_FETCH_TRANSITIONS_KEY, e.toString(), e);

				// Issue Transition SLA
				enhancedSendExperienceAnalytics(
					false,
					projectType,
					`${e.name || ''} ${e.message}`.trim(),
					e.traceId,
					e.statusCode,
					source,
					isAbortableError(e) ? 'taskAbort' : 'taskFail',
				);

				setTransitionsState({
					data: null,
					loading: false,
					error: e,
				});
			}
			isSingletonRequestRef.current = false;
		},
		// we want to fetch the transitions whenever any field values change

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[issueKey, projectType, setTransitionsState, fieldValues, isStatusMoveExperimentEnabled],
	);

	let useProperEffect = fg('relay-migration-issue-fields-status') ? useLayoutEffect : useMemo;

	if (fg('hot-122017--fetch-transitions-in-use-effect')) {
		useProperEffect = useEffect;
	}

	if (fg('hot-122017--fetch-transitions-in-use-layout-effect')) {
		useProperEffect = useLayoutEffect;
	}

	// re-evaluates fetching data again whenever issueKey changes
	// we have to use useLayoutEffect instead of useEffect here because useMemo will be invoked before render
	// we cannot use useMemo in concurrent - if setState within a useMemo concurrent breaks
	// eg, when you have a parent issue and navigate to a child issue - the child issue loads the parent data instead
	// when you use useMemo
	useProperEffect(() => {
		if (preFetch === true && createAnalyticsEvent) {
			fetch(createAnalyticsEvent({}));
		}
	}, [issueKey, preFetch, createAnalyticsEvent, setTransitionsState, fetch]);

	// We need to clear the "data" list whenever the status object changes, because
	// each status has a different list of transitions associated with it.
	// We could achieve a similar behaviour by using a "key" prop when rendering
	// TransitionQuery, but that causes problems with keeping the focus on the component
	// when the status changes. We are using the full status object as "self-transitions"
	// from one status to itself need to exhibit the same behaviour, and it also fixes a bug
	// with PopupSelect downstream
	useEffect(() => {
		if (isSaving) {
			setTransitionsState({
				data: null,
			});
		}
		// component just finished saving, changing from saving to not saving, so we fetch the transitions for the saved status!
		else if (!isSaving && preFetch === true && createAnalyticsEvent) {
			fetch(createAnalyticsEvent({}));
		}
	}, [isSaving, status, preFetch, createAnalyticsEvent, setTransitionsState, fetch]);

	const transitions = useMemo(
		() => (transitionsState.data ? transitionsState.data.map(transformTransitionFromStore) : null),
		[transitionsState.data],
	);

	return props.children({
		...transitionsState,
		data: transitions,
		fetch,
	});
};

const ScopedTransitionsQueryBase = (props: Props) => (
	<WorkflowTransitionsStoreContainer scope={props.issueKey}>
		<BaseTransitionsQuery {...props} />
	</WorkflowTransitionsStoreContainer>
);

export const ScopedTransitionsQuery = (props: Props): React.JSX.Element => {
	const source = useAnalyticsSource();
	return <ScopedTransitionsQueryBase {...props} source={source} />;
};

const transactionsQueryWithAnalytics = withAnalyticsEvents()(ScopedTransitionsQuery);
export { transactionsQueryWithAnalytics as TransitionsQuery };
